import React from 'react'
import styled from 'styled-components'

import {
  useGetCollectionWithItemsOnlyQuery,
  useGetSelectEscapeIndexesQuery,
} from 'hooks/useQueries'

import Button from 'components/display/Button'
import { Grid, GridItem } from '../../../../components/layout/ItemGrid'
import PlaceholderCard from '../../../../components/cards/PlaceholderCard'
import RenderOnlyWhenVisbile from '../../../../components/utils/RenderOnlyWhenVisible'

import { PAGE_MAX_WIDTH } from '../constants'
import Item from './Item'
import { NPlaceholderItems } from './Loading'

const Empty = styled.p`
  text-align: center;
`

const Outer = styled.div`
  max-width: ${PAGE_MAX_WIDTH}px;
  margin: 0 auto;
`

type ItemsProps = {
  collectionId: string
}

const Items = ({ collectionId }: ItemsProps) => {
  const { data: selectEscapeIndexesData } = useGetSelectEscapeIndexesQuery()
  const bucketListItemsIndex = selectEscapeIndexesData?.currentUser
    ?.bucketListItemsIndex as GQL_BucketListItemsIndex
  const bookingAdventuresIndex = selectEscapeIndexesData?.currentUser
    ?.bookingAdventuresIndex as GQL_BookingAdventuresIndex

  const {
    data,
    loading,
    called,
    fetchMore,
  } = useGetCollectionWithItemsOnlyQuery({
    notifyOnNetworkStatusChange: true, // required for `loading` to be `true` while more items are being loaded via fetchMore
    variables: {
      id: collectionId,
      pageSize: 15,
    },
  })

  const edges = data?.collection?.items?.edges
  const noItems = called && !loading && edges?.length === 0

  const pageInfo = data?.collection?.items?.pageInfo

  const fetchMoreItems = () => {
    fetchMore({
      variables: {
        cursor: pageInfo?.endCursor,
      },
    })
  }

  if (noItems) {
    return <Empty>This collection has no items.</Empty>
  }

  return (
    <Outer>
      <Grid>
        {edges?.map((edge) => (
          <GridItem
            key={`${edge.node.item.__typename}:${edge.node.item.id}`}
            widthOverride={{ xxxl: '33.33%' }}
          >
            <RenderOnlyWhenVisbile
              rootMargin="50% 0px"
              placeholder={<PlaceholderCard portrait={false} />}
            >
              <Item
                item={edge.node.item}
                photo={edge.node?.photo}
                bucketListItemsIndex={bucketListItemsIndex}
                bookingAdventuresIndex={bookingAdventuresIndex}
              />
            </RenderOnlyWhenVisbile>
          </GridItem>
        ))}
        {loading && <NPlaceholderItems count={6} />}
      </Grid>

      {pageInfo?.hasNextPage && (
        <Button
          disabled={loading}
          onClick={fetchMoreItems}
          uppercase={false}
          ghost
          style={{ margin: '30px auto 60px auto' }}
        >
          Load More
        </Button>
      )}
    </Outer>
  )
}

export default Items
